// src/App.js
import React, { useState, useEffect } from 'react';
import { auth, db } from './firebaseConfig'; // Import auth and db
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import LoginPage from './components/LoginPage';
import AdminDashboard from './components/AdminDashboard';

function App() {
  const [user, setUser] = useState(null); // Stores Firebase Auth user object
  const [isAdmin, setIsAdmin] = useState(false); // True if logged-in user is an admin
  const [loading, setLoading] = useState(true); // Manages initial loading state

  useEffect(() => {
    // This listener observes Firebase Authentication state changes
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        // User is signed in, now check if they are an admin in Firestore
        console.log('Auth state changed: User is signed in:', authUser.uid);
        try {
          const userDocRef = doc(db, 'users', authUser.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            if (userData.isAdmin) {
              setUser(authUser);
              setIsAdmin(true);
              console.log('User is an admin. Access granted.');
            } else {
              // Not an admin, log them out
              console.warn('User is not an admin. Logging out.');
              await auth.signOut();
              setUser(null);
              setIsAdmin(false);
            }
          } else {
            // User document not found in Firestore, log them out
            console.error('User document not found for UID:', authUser.uid, '. Logging out.');
            await auth.signOut();
            setUser(null);
            setIsAdmin(false);
          }
        } catch (error) {
          console.error('Error checking admin status:', error);
          await auth.signOut(); // Log out on error
          setUser(null);
          setIsAdmin(false);
        }
      } else {
        // User is signed out
        console.log('Auth state changed: User is signed out.');
        setUser(null);
        setIsAdmin(false);
      }
      setLoading(false); // Authentication check is complete
    });

    // Clean up the listener when the component unmounts
    return () => unsubscribe();
  }, []);

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading admin panel...</p>
      </div>
    );
  }

  // Render LoginPage if not an admin, else render AdminDashboard
  return (
    <div style={styles.appContainer}>
      {isAdmin ? (
        <AdminDashboard user={user} />
      ) : (
        <LoginPage onLoginSuccess={(loggedInUser) => {
          // This callback is triggered from LoginPage after successful admin login
          setUser(loggedInUser);
          setIsAdmin(true);
        }} />
      )}
    </div>
  );
}

const styles = {
  appContainer: {
    // Basic styling for the entire app container
    minHeight: '100vh',
    backgroundColor: '#f0f2f5',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f0f2f5',
    fontSize: '20px',
    color: '#555',
  },
  spinner: {
    border: '4px solid rgba(0, 0, 0, 0.1)',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    borderLeftColor: '#007bff',
    animation: 'spin 1s ease infinite',
    marginBottom: '20px',
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
};

export default App;
